/* eslint-disable import/no-cycle */
import { BaseEntityModel } from '@/modules/core/app/models/abstracts/BaseEntityModel';
import { User } from '@/modules/user/models/User';
import { Jurisdiction } from '@/modules/predefined/models/Jurisdiction';
import { BillingPlanMapping } from '@/modules/billing/models/BillingPlanMapping';
import { EstateRole } from '@/modules/role/models/EstateRole';
import { Profession } from '@/modules/professional/models/Profession';
import { AnnouncementStatusKey } from '@/modules/announcement/constants/AnnouncementStatusKey';
import { AnnouncementContentType } from '@/modules/announcement/constants/AnnouncementContentType';
import dayjs from 'dayjs';
import { DateFormat } from '@/modules/core/app/constants/date.constants';
import { Country } from '@/modules/predefined/models/Country';
import Lang from '@/modules/core/app/services/Lang';
import { PredefinedCountry } from '@/modules/predefined/enums/PredefinedCountry';
import { AnnouncementJurisdictionScopeTypeKey } from '@/modules/announcement/constants/AnnouncementJurisdictionScopeTypeKey';
import { AnnouncementRoleScopeTypeKey } from '@/modules/announcement/constants/AnnouncementRoleScopeTypeKey';
import { AnnouncementBillingPlanScopeTypeKey } from '@/modules/announcement/constants/AnnouncementBillingPlanScopeTypeKey';
import { Company } from '@/modules/company/models/Company';

export class Announcement extends BaseEntityModel {
  id: number;
  title: string;
  description: string;
  releaseDate: string;
  contentType: AnnouncementContentType;
  contentSource: string;
  contentSourceUrl: string;
  status: AnnouncementStatusKey;
  urlScope: string;
  isAllJurisdictions: boolean;
  isAllBillingPlanMappings: boolean;
  isAllEstateRoles: boolean;
  isAllProfessions: boolean;
  lastEditedByUser?: Relationship<User> | null;
  jurisdictions: Relationship<Jurisdiction[]>;
  billingPlanMappings: Relationship<BillingPlanMapping[]>;
  estateRoles: Relationship<EstateRole[]>;
  professions: Relationship<Profession[]>;
  countries: Relationship<Country[]>;
  excludedCompanies: Relationship<Company[]>;

  constructor(model: Partial<Announcement> = {}) {
    super(model);

    this.id = model.id as number;
    this.title = model.title as string;
    this.description = model.description as string;
    this.releaseDate = model.releaseDate as string;
    this.contentType = model.contentType as AnnouncementContentType;
    this.contentSource = model.contentSource as string;
    this.contentSourceUrl = model.contentSourceUrl as string;
    this.status = model.status as AnnouncementStatusKey;
    this.urlScope = model.urlScope as string;
    this.isAllJurisdictions = model.isAllJurisdictions as boolean;
    this.isAllBillingPlanMappings = model.isAllBillingPlanMappings as boolean;
    this.isAllEstateRoles = model.isAllEstateRoles as boolean;
    this.isAllProfessions = model.isAllProfessions as boolean;
    this.lastEditedByUser = User.mapModelOrNull(model.lastEditedByUser);
    this.jurisdictions = Jurisdiction.mapModels(model.jurisdictions);
    this.billingPlanMappings = BillingPlanMapping.mapModels(model.billingPlanMappings);
    this.estateRoles = EstateRole.mapModels(model.estateRoles);
    this.professions = Profession.mapModels(model.professions);
    this.countries = Country.mapModels(model.countries);
    this.excludedCompanies = Company.mapModels(model.excludedCompanies) ?? [];
  }

  public isAllRoles(): boolean {
    return (this.isAllProfessions && this.isAllEstateRoles) ?? false;
  }

  get formattedReleaseDate(): string {
    return dayjs(this.releaseDate).format(DateFormat.LOCALIZED_DAY_MONTH_YEAR);
  }

  get formattedUpdatedAt(): string {
    return dayjs(this.updatedAt).format(DateFormat.LOCALIZED_DAY_MONTH_YEAR);
  }

  get previewTitle(): string {
    if (!this.title || this.title === '') {
      return `{{ ${Lang.t('announcement.title-placeholder')} }}`;
    }

    return this.title;
  }

  get previewDescription(): string {
    if (!this.description || this.description === '') {
      return `{{ ${Lang.t('announcement.description-placeholder')} }}`;
    }

    return this.description;
  }

  get appliesToCanada(): boolean {
    return (
      this.countries?.map((country: Country) => country.id).includes(PredefinedCountry.CANADA.id) ??
      false
    );
  }

  get appliesToUSA(): boolean {
    return (
      this.countries
        ?.map((country: Country) => country.id)
        .includes(PredefinedCountry.UNITED_STATES.id) ?? false
    );
  }

  public setJurisdictionScope(key: AnnouncementJurisdictionScopeTypeKey): void {
    this.jurisdictions = [];
    this.countries = [];
    this.isAllJurisdictions = false;

    switch (key) {
      case AnnouncementJurisdictionScopeTypeKey.ALL:
        this.isAllJurisdictions = true;
        break;
      case AnnouncementJurisdictionScopeTypeKey.CANADA:
        this.countries = [PredefinedCountry.CANADA];
        break;
      case AnnouncementJurisdictionScopeTypeKey.USA:
        this.countries = [PredefinedCountry.UNITED_STATES];
        break;
      case AnnouncementJurisdictionScopeTypeKey.SELECT:
        break;
    }
  }

  get jurisdictionScopeTypeKey(): AnnouncementJurisdictionScopeTypeKey | null {
    if (this.isAllJurisdictions) {
      return AnnouncementJurisdictionScopeTypeKey.ALL;
    }

    if (this.appliesToCanada) {
      return AnnouncementJurisdictionScopeTypeKey.CANADA;
    }

    if (this.appliesToUSA) {
      return AnnouncementJurisdictionScopeTypeKey.USA;
    }

    if (this.jurisdictions?.length > 0) {
      return AnnouncementJurisdictionScopeTypeKey.SELECT;
    }

    return null;
  }

  public setRoleScope(key: AnnouncementRoleScopeTypeKey): void {
    this.professions = [];
    this.estateRoles = [];
    this.isAllEstateRoles = false;
    this.isAllProfessions = false;

    switch (key) {
      case AnnouncementRoleScopeTypeKey.ALL:
        this.isAllEstateRoles = true;
        this.isAllProfessions = true;
        break;
      case AnnouncementRoleScopeTypeKey.ALL_ESTATE_ROLES:
        this.isAllEstateRoles = true;
        break;
      case AnnouncementRoleScopeTypeKey.ALL_PROFESSIONS:
        this.isAllProfessions = true;
        break;
      case AnnouncementRoleScopeTypeKey.SELECT:
        break;
    }
  }

  get roleScopeTypeKey(): AnnouncementRoleScopeTypeKey | null {
    if (this.isAllEstateRoles && this.isAllProfessions) {
      return AnnouncementRoleScopeTypeKey.ALL;
    }

    if (this.isAllEstateRoles) {
      return AnnouncementRoleScopeTypeKey.ALL_ESTATE_ROLES;
    }

    if (this.isAllProfessions) {
      return AnnouncementRoleScopeTypeKey.ALL_PROFESSIONS;
    }

    if (this.professions?.length > 0 || this.estateRoles?.length > 0) {
      return AnnouncementRoleScopeTypeKey.SELECT;
    }

    return null;
  }

  setBillingPlanMappingScope(key: AnnouncementBillingPlanScopeTypeKey): void {
    this.billingPlanMappings = [];
    this.isAllBillingPlanMappings = false;

    switch (key) {
      case AnnouncementBillingPlanScopeTypeKey.ALL:
        this.isAllBillingPlanMappings = true;
        break;
      case AnnouncementBillingPlanScopeTypeKey.SELECT:
        break;
    }
  }

  get billingPlanMappingScopeTypeKey(): AnnouncementBillingPlanScopeTypeKey | null {
    if (this.isAllBillingPlanMappings) {
      return AnnouncementBillingPlanScopeTypeKey.ALL;
    }

    if (this.billingPlanMappings?.length > 0) {
      return AnnouncementBillingPlanScopeTypeKey.SELECT;
    }

    return null;
  }
}
